import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ANCHOR_DAY_OPTIONS,
  ANCHOR_OPTIONS,
  CONDITION_OPTIONS,
  type ConditionKind,
  type DayOfAnchorMonth,
  type DeadlineAnchor,
  type DeadlineCondition,
  type DeadlineEvent,
  type DeadlineRecurrence,
  type DeadlineRule,
  type DeadlineRuleKind,
  type DeadlineExtension,
  EVENT_OPTIONS,
  FILING_STATUS_OPTIONS,
  FILING_TYPE_OPTIONS,
  FOUNDATION_STATUS_OPTIONS,
  type FilingStatus,
  type FilingType,
  type FoundationStatus,
  formatCondition,
  formatDeadlineRule,
  makeEmptyCondition,
  MONTHS,
  PARITY_OPTIONS,
  RECURRENCE_OPTIONS,
  RULE_KIND_OPTIONS,
  type RecurrenceKind,
  defaultAnchorForKind,
  makeEmptyRule,
  resolveExtensions,
  ruleSupportsAnchor,
} from '../lib/deadlineRules';

interface Props {
  value: DeadlineRule[];
  onChange: (next: DeadlineRule[]) => void;
  helperText?: string;
}

function numberOrUndef(raw: string): number | undefined {
  if (raw === '' || raw === '-') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function isAnchorRequired(kind: DeadlineRuleKind): boolean {
  return ruleSupportsAnchor(kind) || kind === 'inAnchorMonth' || kind === 'wholeAnchorQuarter';
}

// ─── Anchor picker (shared by every kind that needs an anchor) ───────────

function AnchorPicker({
  rule,
  onChange,
}: {
  rule: DeadlineRule;
  onChange: (next: DeadlineRule) => void;
}) {
  if (!isAnchorRequired(rule.kind)) return null;
  const value = rule.anchor ?? defaultAnchorForKind(rule.kind) ?? 'fiscalYearEnd';
  // Legacy kinds (afterFiscalYearEnd / nthDayOfMonthAfterFYE) hard-pin anchor=FYE.
  // We still surface a read-only field so the user can convert by switching kind.
  const locked = rule.kind === 'afterFiscalYearEnd' || rule.kind === 'nthDayOfMonthAfterFYE';
  return (
    <TextField
      select
      label="Anchor"
      size="small"
      value={value}
      disabled={locked}
      helperText={locked ? 'Locked to FYE — switch to "after anchor" kind to change' : undefined}
      onChange={(e) => onChange({ ...rule, anchor: e.target.value as DeadlineAnchor })}
      sx={{ minWidth: 200 }}
    >
      {ANCHOR_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

// ─── Recurrence + parity pickers ─────────────────────────────────────────

function RecurrencePicker({
  rule,
  onChange,
}: {
  rule: DeadlineRule;
  onChange: (next: DeadlineRule) => void;
}) {
  const recurrence = rule.recurrence ?? { kind: 'annual' as RecurrenceKind };
  const setKind = (k: RecurrenceKind) => {
    if (k === 'biennial') onChange({ ...rule, recurrence: { kind: 'biennial' } });
    else onChange({ ...rule, recurrence: { kind: k } as DeadlineRecurrence });
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        select
        label="Recurrence"
        size="small"
        value={recurrence.kind}
        onChange={(e) => setKind(e.target.value as RecurrenceKind)}
        sx={{ minWidth: 200 }}
      >
        {RECURRENCE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      {recurrence.kind === 'biennial' && (
        <TextField
          select
          label="Parity"
          size="small"
          value={(recurrence as { parity?: string }).parity ?? ''}
          onChange={(e) =>
            onChange({
              ...rule,
              recurrence: e.target.value
                ? { kind: 'biennial', parity: e.target.value as 'odd' | 'even' | 'fromFormationYear' }
                : { kind: 'biennial' },
            })
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">No parity (every 2 years from horizon start)</MenuItem>
          {PARITY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

// ─── Conditions accordion ────────────────────────────────────────────────

function ConditionsEditor({
  rule,
  onChange,
}: {
  rule: DeadlineRule;
  onChange: (next: DeadlineRule) => void;
}) {
  const conditions = rule.conditions ?? [];
  const setConditions = (next: DeadlineCondition[]) =>
    onChange({ ...rule, conditions: next.length ? next : undefined });

  const update = (idx: number, c: DeadlineCondition) => {
    const copy = conditions.slice();
    copy[idx] = c;
    setConditions(copy);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        mt: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Conditions
          </Typography>
          <Chip
            size="small"
            label={conditions.length === 0 ? 'always applies' : `${conditions.length} AND`}
            variant="outlined"
            color={conditions.length > 0 ? 'primary' : 'default'}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {conditions.map((c, idx) => (
            <Box
              key={idx}
              sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  label="Condition"
                  size="small"
                  value={c.kind}
                  onChange={(e) => update(idx, makeEmptyCondition(e.target.value as ConditionKind))}
                  sx={{ minWidth: 220 }}
                >
                  {CONDITION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                {(c.kind === 'revenueAtLeast' ||
                  c.kind === 'revenueBelow' ||
                  c.kind === 'assetsAtLeast' ||
                  c.kind === 'assetsBelow') && (
                  <TextField
                    type="number"
                    label="Amount (USD)"
                    size="small"
                    value={c.amount}
                    onChange={(e) => update(idx, { ...c, amount: Number(e.target.value) || 0 })}
                    sx={{ width: 160 }}
                  />
                )}
                {(c.kind === 'entityTypeIn' || c.kind === 'entityTypeNotIn') && (
                  <TextField
                    label="Entity keys"
                    helperText="Comma-separated, e.g. 501c3, 501c4"
                    size="small"
                    value={(c.entityKeys ?? []).join(', ')}
                    onChange={(e) =>
                      update(idx, {
                        ...c,
                        entityKeys: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    sx={{ minWidth: 240, flex: 1 }}
                  />
                )}
                {c.kind === 'fiscalYearEndIs' && (
                  <>
                    <TextField
                      select
                      label="Month"
                      size="small"
                      value={c.month}
                      onChange={(e) => update(idx, { ...c, month: Number(e.target.value) })}
                      sx={{ minWidth: 140 }}
                    >
                      {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      type="number"
                      label="Day"
                      size="small"
                      value={c.day}
                      onChange={(e) => update(idx, { ...c, day: Number(e.target.value) || 1 })}
                      inputProps={{ min: 1, max: 31 }}
                      sx={{ width: 100 }}
                    />
                  </>
                )}
                {c.kind === 'foundationStatusIs' && (
                  <TextField
                    select
                    label="Status"
                    size="small"
                    value={c.status}
                    onChange={(e) =>
                      update(idx, { ...c, status: e.target.value as FoundationStatus })
                    }
                    helperText="Unanswered does not filter — both are shown"
                    sx={{ minWidth: 200 }}
                  >
                    {FOUNDATION_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {c.kind === 'filingStatusIs' && (
                  <TextField
                    select
                    SelectProps={{ multiple: true }}
                    label="Applies while"
                    size="small"
                    value={Array.isArray(c.status) ? c.status : [c.status]}
                    onChange={(e) =>
                      update(idx, {
                        ...c,
                        status: e.target.value as unknown as FilingStatus[],
                      })
                    }
                    helperText="Unanswered is treated as current"
                    sx={{ minWidth: 240 }}
                  >
                    {FILING_STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {c.kind === 'filingTypeIs' && (
                  <TextField
                    select
                    label="Filing type"
                    size="small"
                    value={c.filingType}
                    onChange={(e) => update(idx, { ...c, filingType: e.target.value as FilingType })}
                    helperText="Unanswered is treated as a renewal"
                    sx={{ minWidth: 220 }}
                  >
                    {FILING_TYPE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {c.kind === 'flag' && (
                  <TextField
                    label="Flag name"
                    size="small"
                    value={c.flag ?? ''}
                    onChange={(e) => update(idx, { ...c, flag: e.target.value })}
                    sx={{ minWidth: 200 }}
                  />
                )}
                <Box flexGrow={1} />
                <IconButton
                  size="small"
                  onClick={() => setConditions(conditions.filter((_, i) => i !== idx))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Preview: <strong>{formatCondition(c)}</strong>
              </Typography>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
            onClick={() => setConditions([...conditions, makeEmptyCondition('revenueAtLeast')])}
          >
            Add condition (joined by AND)
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Extension + grace period ────────────────────────────────────────────

function ExtensionEditor({
  rule,
  onChange,
}: {
  rule: DeadlineRule;
  onChange: (next: DeadlineRule) => void;
}) {
  // Read through resolveExtensions so a record still carrying the old singular
  // `extension` edits normally; writing always uses the chained form, and the
  // singular field is cleared so the two can never disagree.
  const chain = resolveExtensions(rule);

  const setChain = (next: DeadlineExtension[]) => {
    const kept = next.filter((e) => e.offset && (e.offset.months || e.offset.days || e.offset.weeks));
    onChange({
      ...rule,
      extensions: kept.length > 0 ? kept : undefined,
      extension: undefined,
    });
  };

  const updateAt = (idx: number, patch: Partial<DeadlineExtension>) =>
    setChain(chain.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  const setOffsetAt = (idx: number, key: 'months' | 'days', raw: string) =>
    updateAt(idx, { offset: { ...chain[idx].offset, [key]: numberOrUndef(raw) } });

  const summary = chain
    .map((e) => `+${e.offset?.months ?? 0}mo/${e.offset?.days ?? 0}d`)
    .join(' then ');

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        mt: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Extensions &amp; grace period
          </Typography>
          {chain.length > 0 && (
            <Chip size="small" variant="outlined" color="primary" label={summary} />
          )}
          {!!rule.gracePeriodDays && (
            <Chip size="small" variant="outlined" label={`${rule.gracePeriodDays}d grace`} />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {chain.map((ext, idx) => (
            <Box key={idx} sx={{ p: 1.5, borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="caption" fontWeight={700}>
                  {idx === 0 ? 'First extension' : `Extension ${idx + 1}`}
                </Typography>
                {idx > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    runs from the end of extension {idx}, not from the original deadline
                  </Typography>
                )}
                <Box flexGrow={1} />
                <IconButton size="small" onClick={() => setChain(chain.filter((_, i) => i !== idx))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField
                  label="Extension form"
                  size="small"
                  value={ext.form ?? ''}
                  onChange={(e) => updateAt(idx, { form: e.target.value || undefined })}
                  sx={{ minWidth: 180, flex: 1 }}
                />
                <TextField
                  type="number"
                  label="Extra months"
                  size="small"
                  value={ext.offset?.months ?? ''}
                  onChange={(e) => setOffsetAt(idx, 'months', e.target.value)}
                  sx={{ width: 130 }}
                />
                <TextField
                  type="number"
                  label="Extra days"
                  size="small"
                  value={ext.offset?.days ?? ''}
                  onChange={(e) => setOffsetAt(idx, 'days', e.target.value)}
                  sx={{ width: 130 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={ext.automatic !== false}
                      onChange={(_, checked) => updateAt(idx, { automatic: checked })}
                    />
                  }
                  label="Automatic"
                />
              </Stack>
              <TextField
                label="Extension description"
                size="small"
                fullWidth
                sx={{ mt: 1 }}
                value={ext.description ?? ''}
                onChange={(e) => updateAt(idx, { description: e.target.value || undefined })}
              />
            </Box>
          ))}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setChain([...chain, { offset: { months: 3 } }])}
            >
              {chain.length === 0 ? 'Add extension' : 'Add another extension window'}
            </Button>
            {chain.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                A few states grant a second window that runs inside the first — Tennessee allows
                three months, then another three within it, landing six months past the deadline.
              </Typography>
            )}
          </Box>

          <TextField
            type="number"
            label="Grace period (days)"
            size="small"
            helperText="Days after the deadline a filing is still considered timely"
            value={rule.gracePeriodDays ?? ''}
            onChange={(e) => onChange({ ...rule, gracePeriodDays: numberOrUndef(e.target.value) })}
            sx={{ maxWidth: 260 }}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function RuleEditor({
  rule,
  onChange,
  onRemove,
}: {
  rule: DeadlineRule;
  onChange: (next: DeadlineRule) => void;
  onRemove: () => void;
}) {
  const [showLabel, setShowLabel] = useState(!!rule.label);

  const setKind = (kind: DeadlineRuleKind) => {
    if (kind !== rule.kind) {
      // Carry across cross-cutting metadata when switching kinds.
      const carry = {
        id: rule.id,
        label: rule.label,
        recurrence: rule.recurrence,
        conditions: rule.conditions,
        extension: rule.extension,
        gracePeriodDays: rule.gracePeriodDays,
      };
      onChange({ ...makeEmptyRule(kind), ...carry });
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TextField
          select
          label="Type"
          size="small"
          value={rule.kind}
          onChange={(e) => setKind(e.target.value as DeadlineRuleKind)}
          sx={{ minWidth: 240 }}
        >
          {RULE_KIND_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <AnchorPicker rule={rule} onChange={onChange} />

        {rule.kind === 'fixedDate' && (
          <>
            <TextField
              select
              label="Month"
              size="small"
              value={rule.month}
              onChange={(e) => onChange({ ...rule, month: Number(e.target.value) })}
              sx={{ minWidth: 140 }}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label="Day"
              size="small"
              value={rule.day}
              onChange={(e) => onChange({ ...rule, day: Number(e.target.value) })}
              inputProps={{ min: 1, max: 31 }}
              sx={{ width: 100 }}
            />
          </>
        )}

        {(rule.kind === 'afterFiscalYearEnd' || rule.kind === 'afterAnchor') && (
          <>
            <TextField
              type="number"
              label="Months"
              size="small"
              value={rule.months ?? ''}
              onChange={(e) => onChange({ ...rule, months: numberOrUndef(e.target.value) })}
              inputProps={{ min: 0, step: 0.5 }}
              sx={{ width: 110 }}
              helperText="Decimals allowed"
            />
            <TextField
              type="number"
              label="Weeks"
              size="small"
              value={rule.weeks ?? ''}
              onChange={(e) => onChange({ ...rule, weeks: numberOrUndef(e.target.value) })}
              inputProps={{ min: 0 }}
              sx={{ width: 100 }}
              helperText="Optional"
            />
            <TextField
              type="number"
              label="Days"
              size="small"
              value={rule.days ?? ''}
              onChange={(e) => onChange({ ...rule, days: numberOrUndef(e.target.value) })}
              inputProps={{ min: 0 }}
              sx={{ width: 100 }}
              helperText="Optional"
            />
          </>
        )}

        {(rule.kind === 'nthDayOfMonthAfterFYE' || rule.kind === 'nthDayOfMonthAfterAnchor') && (
          <>
            <TextField
              type="number"
              label="Day of month"
              size="small"
              value={rule.dayOfMonth}
              onChange={(e) => onChange({ ...rule, dayOfMonth: Number(e.target.value) })}
              inputProps={{ min: 1, max: 31 }}
              sx={{ width: 130 }}
            />
            <Box display="flex" alignItems="center" px={0.5}>
              <Typography variant="body2" color="text.secondary">
                of
              </Typography>
            </Box>
            <TextField
              type="number"
              label="Month #"
              size="small"
              value={rule.monthOffset}
              onChange={(e) => onChange({ ...rule, monthOffset: Number(e.target.value) })}
              inputProps={{ min: 1, max: 12 }}
              sx={{ width: 110 }}
            />
          </>
        )}

        {rule.kind === 'inAnchorMonth' && (
          <>
            <DayOfAnchorMonthControl
              value={rule.anchorDayOfMonth ?? 'last'}
              onChange={(v) => onChange({ ...rule, anchorDayOfMonth: v })}
            />
            <TextField
              type="number"
              label="Month offset"
              size="small"
              value={rule.anchorMonthOffset ?? ''}
              onChange={(e) =>
                onChange({ ...rule, anchorMonthOffset: numberOrUndef(e.target.value) })
              }
              sx={{ width: 130 }}
              helperText="Optional"
            />
          </>
        )}

        {rule.kind === 'wholeAnchorQuarter' && (
          <TextField
            type="number"
            label="Quarter offset"
            size="small"
            value={rule.anchorQuarterOffset ?? ''}
            onChange={(e) =>
              onChange({ ...rule, anchorQuarterOffset: numberOrUndef(e.target.value) })
            }
            sx={{ width: 140 }}
            helperText="0 = same quarter"
          />
        )}

        {rule.kind === 'event' && (
          <>
            <TextField
              select
              label="Event"
              size="small"
              value={rule.event}
              onChange={(e) => onChange({ ...rule, event: e.target.value as DeadlineEvent })}
              sx={{ minWidth: 280 }}
            >
              {EVENT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            {rule.event === 'custom' && (
              <TextField
                label="Description"
                size="small"
                value={rule.description ?? ''}
                onChange={(e) => onChange({ ...rule, description: e.target.value })}
                sx={{ minWidth: 280, flex: 1 }}
              />
            )}
          </>
        )}

        <Box flexGrow={1} />
        <Tooltip title="Remove condition">
          <IconButton size="small" onClick={onRemove} color="default">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box mt={1.5}>
        <RecurrencePicker rule={rule} onChange={onChange} />
      </Box>

      {showLabel ? (
        <TextField
          label="Label (optional)"
          helperText="Used in calendars and reports, e.g. 'Form 990 filing'"
          size="small"
          fullWidth
          value={rule.label ?? ''}
          onChange={(e) => onChange({ ...rule, label: e.target.value || undefined })}
          sx={{ mt: 1.5 }}
        />
      ) : (
        <Button
          size="small"
          onClick={() => setShowLabel(true)}
          sx={{ mt: 1, textTransform: 'none' }}
          startIcon={<AddIcon fontSize="small" />}
        >
          Add label
        </Button>
      )}

      <ConditionsEditor rule={rule} onChange={onChange} />
      <ExtensionEditor rule={rule} onChange={onChange} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Preview: <strong>{formatDeadlineRule(rule)}</strong>
      </Typography>
    </Box>
  );
}

function DayOfAnchorMonthControl({
  value,
  onChange,
}: {
  value: DayOfAnchorMonth;
  onChange: (v: DayOfAnchorMonth) => void;
}) {
  // Two-pane control: a dropdown for sentinel choices, plus a numeric option.
  const isNumber = typeof value === 'number';
  const [mode, setMode] = useState<'sentinel' | 'number'>(isNumber ? 'number' : 'sentinel');

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        select
        label="Day"
        size="small"
        value={mode === 'number' ? '__number__' : (value as string)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__number__') {
            setMode('number');
            onChange(1);
          } else {
            setMode('sentinel');
            onChange(v as DayOfAnchorMonth);
          }
        }}
        sx={{ minWidth: 200 }}
      >
        {ANCHOR_DAY_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
        <MenuItem value="__number__">Specific day number…</MenuItem>
      </TextField>
      {mode === 'number' && (
        <TextField
          type="number"
          label="Day #"
          size="small"
          value={isNumber ? value : 1}
          onChange={(e) => onChange(Number(e.target.value) || 1)}
          inputProps={{ min: 1, max: 31 }}
          sx={{ width: 100 }}
        />
      )}
    </Stack>
  );
}

export default function DeadlineRulesInput({ value, onChange, helperText }: Props) {
  const rules = Array.isArray(value) ? value : [];

  const update = (idx: number, next: DeadlineRule) => {
    const copy = rules.slice();
    copy[idx] = next;
    onChange(copy);
  };

  const remove = (idx: number) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...rules, makeEmptyRule('afterFiscalYearEnd')]);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle2">Deadline Rules</Typography>
        <Chip size="small" label={`${rules.length} condition${rules.length === 1 ? '' : 's'}`} />
      </Stack>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {helperText}
        </Typography>
      )}

      <Stack spacing={1}>
        {rules.map((rule, idx) => (
          <Box key={idx}>
            {idx > 0 && (
              <Box display="flex" justifyContent="center" my={0.5}>
                <Chip size="small" label="OR" color="primary" variant="outlined" />
              </Box>
            )}
            <RuleEditor
              rule={rule}
              onChange={(next) => update(idx, next)}
              onRemove={() => remove(idx)}
            />
          </Box>
        ))}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        onClick={add}
        size="small"
        sx={{ mt: 1.5 }}
        variant="outlined"
      >
        {rules.length === 0 ? 'Add deadline rule' : 'Add OR condition'}
      </Button>
    </Box>
  );
}
